import React, { useEffect, useState } from 'react';
import apiClient from '../api/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Plus, Edit, Trash2, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { getCurrencySymbol, formatCurrency } from '../utils/currency';
import ClientModal from '../components/modals/ClientModal';
import TopAlert from '../components/TopAlert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const Clients = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [pageAlert, setPageAlert] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 9;

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await apiClient.get('/clients');
      setClients(response.data);
      setPageAlert('');
    } catch (error) {
      setPageAlert('Failed to load clients. Please refresh and try again.');
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setSelectedClient(null);
    setModalOpen(true);
  };

  const handleEdit = (client) => {
    setSelectedClient(client);
    setModalOpen(true);
  };

  const handleDeleteClick = (client) => {
    setClientToDelete(client);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await apiClient.delete(`/clients/${clientToDelete.client_id}`);
      toast.success('Client deleted successfully');
      fetchClients();
      setDeleteDialogOpen(false);
      setClientToDelete(null);
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const filteredClients = clients.filter((client) => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return true;
    return (
      client.name?.toLowerCase().includes(query) ||
      client.email?.toLowerCase().includes(query) ||
      client.company?.toLowerCase().includes(query)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filteredClients.length / perPage));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * perPage;
  const pagedClients = filteredClients.slice(startIndex, startIndex + perPage);

  return (
    <>
      <div className="space-y-6" data-testid="clients-page">
        <TopAlert message={pageAlert} tone="error" onClose={() => setPageAlert('')} />
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Clients</h1>
            <p className="text-slate-600 mt-1">Manage your client relationships</p>
          </div>
          <Button
            onClick={handleAdd}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="add-client-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Client
          </Button>
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            placeholder="Search clients by name, email, or company"
            className="w-full md:max-w-md px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"
          />
          <div className="text-sm text-slate-500">
            Showing {filteredClients.length === 0 ? 0 : startIndex + 1} - {Math.min(startIndex + perPage, filteredClients.length)} of {filteredClients.length}
          </div>
        </div>

        {filteredClients.length === 0 ? (
          <Card className="border-slate-100">
            <CardContent className="py-12">
              <div className="text-center">
                <p className="text-slate-500 mb-4">
                  {clients.length === 0 ? 'No clients yet. Start by adding your first client!' : 'No clients match your search.'}
                </p>
                <Button
                  onClick={handleAdd}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your First Client
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pagedClients.map((client) => (
              <Card
                key={client.client_id}
                className="border-slate-100 hover:shadow-lg transition-shadow cursor-pointer"
                data-testid={`client-card-${client.client_id}`}
                onClick={() => navigate(`/clients/${client.client_id}`)}
              >
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-lg">{client.name}</CardTitle>
                    <div className="flex space-x-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); navigate(`/clients/${client.client_id}`); }}
                        className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleEdit(client); }}
                        data-testid={`edit-client-${client.client_id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleDeleteClick(client); }}
                        className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                        data-testid={`delete-client-${client.client_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                    <CardDescription className="text-sm">{client?.notes}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600">{client.email}</p>
                  {client.company && <p className="text-sm text-slate-500 mt-1">{client.company}</p>}
                  {client.phone && (
                    <p className="text-sm text-slate-500 mt-1">{client.phone}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {filteredClients.length > perPage && (
          <div className="flex items-center justify-between pt-2">
            <div className="text-sm text-slate-500">Page {safePage} of {totalPages}</div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={safePage === 1}
              >
                Prev
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={safePage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      <ClientModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        client={selectedClient}
        onSuccess={fetchClients}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {clientToDelete?.name}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-rose-600 hover:bg-rose-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default Clients;
